// frontend/src/utils/reference/referenceTracker.js

function normalizeText(value = '') {
  return String(value || '').toLowerCase().trim();
}

function normalizeMessageText(value = '') {
  return normalizeText(value)
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeRegExp(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildWordBoundaryPattern(value = '') {
  const escaped = escapeRegExp(normalizeText(value));
  if (!escaped) return null;

  return new RegExp(`\\b${escaped}\\b`, 'i');
}

function buildPossessivePattern(value = '') {
  const escaped = escapeRegExp(normalizeText(value));
  if (!escaped) return null;

  return new RegExp(`\\b${escaped}(?:'s|s)\\b`, 'i');
}

function getRelationshipRole(person = {}) {
  return (
    person.relationship ||
    person.relationship_type ||
    person.relationshipType ||
    null
  );
}

function buildPersonDisplayName(person = {}) {
  return [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
}

function buildRelationshipCandidate(person = {}, options = {}) {
  const name = buildPersonDisplayName(person);
  if (!name) return null;

  return {
    entityId: person.entityId || null,
    type: 'person',
    name,
    firstName: person.first_name || null,
    lastName: person.last_name || null,
    role: getRelationshipRole(person),
    source: options.source || 'relationship_match',
    signals: {
      explicitlyNamed: Boolean(options.explicitlyNamed),
      pronounSupported: Boolean(options.pronounSupported),
      carryForward: Boolean(options.carryForward),
    },
    metadata: {
      relationshipId: person.relationship_id || person.id || null,
      matchedOn: options.matchedOn || null,
      matchedText: options.matchedText || null,
      possessive: Boolean(options.possessive),
    },
  };
}

function getRelationshipNameForms(person = {}) {
  const firstName = normalizeText(person.first_name || '');
  const lastName = normalizeText(person.last_name || '');
  const fullName = normalizeText(
    [person.first_name, person.last_name].filter(Boolean).join(' ')
  );

  return {
    firstName,
    lastName,
    fullName,
  };
}

function findExplicitRelationshipMention(message = '', relationships = []) {
  const normalizedMessage = normalizeMessageText(message);

  if (
    !normalizedMessage ||
    !Array.isArray(relationships) ||
    relationships.length === 0
  ) {
    return null;
  }

  const fullNameMatches = relationships
    .map((person) => {
      const { fullName } = getRelationshipNameForms(person);
      if (!fullName) return null;

      const exactPattern = buildWordBoundaryPattern(fullName);
      if (!exactPattern) return null;

      if (!exactPattern.test(normalizedMessage)) return null;

      return buildRelationshipCandidate(person, {
        source: 'explicit_full_name_match',
        explicitlyNamed: true,
        matchedOn: 'full_name',
        matchedText: fullName,
      });
    })
    .filter(Boolean);

  if (fullNameMatches.length > 0) {
    return fullNameMatches[0];
  }

  const firstNameMatches = relationships
    .map((person) => {
      const { firstName } = getRelationshipNameForms(person);
      if (!firstName) return null;

      const exactPattern = buildWordBoundaryPattern(firstName);
      const possessivePattern = buildPossessivePattern(firstName);

      const exactMatched = exactPattern?.test(normalizedMessage) === true;
      const possessiveMatched =
        possessivePattern?.test(normalizedMessage) === true;

      if (!exactMatched && !possessiveMatched) {
        return null;
      }

      return buildRelationshipCandidate(person, {
        source: possessiveMatched
          ? 'explicit_first_name_possessive_match'
          : 'explicit_first_name_match',
        explicitlyNamed: true,
        matchedOn: 'first_name',
        matchedText: firstName,
        possessive: possessiveMatched,
      });
    })
    .filter(Boolean);

  if (firstNameMatches.length === 1) {
    return firstNameMatches[0];
  }

  return null;
}

function hasPronounReference(message = '') {
  const lower = ` ${normalizeMessageText(message)} `;

  return [
    ' he ',
    ' she ',
    ' they ',
    ' him ',
    ' her ',
    ' them ',
    ' his ',
    ' hers ',
    ' their ',
    ' theirs ',
    ' what about her ',
    ' what about him ',
    ' what about them ',
  ].some((token) => lower.includes(token));
}

function buildCarryForwardCandidate(currentEntity = null) {
  if (!currentEntity) return null;

  return {
    ...currentEntity,
    source: currentEntity?.source || 'carry_forward',
    signals: {
      ...(currentEntity?.signals || {}),
      carryForward: true,
    },
  };
}

function resolveLastReferencedEntity({
  message = '',
  relationships = [],
  currentEntity = null,
} = {}) {
  const explicitMatch = findExplicitRelationshipMention(message, relationships);
  if (explicitMatch) {
    return explicitMatch;
  }

  if (hasPronounReference(message) && currentEntity) {
    return {
      ...currentEntity,
      source: 'pronoun_carryover',
      signals: {
        ...(currentEntity?.signals || {}),
        pronounSupported: true,
      },
    };
  }

  return buildCarryForwardCandidate(currentEntity);
}

function buildReferenceState({
  message = '',
  relationships = [],
  currentEntity = null,
} = {}) {
  const lastReferencedEntity = resolveLastReferencedEntity({
    message,
    relationships,
    currentEntity,
  });

  return {
    lastReferencedEntity,
  };
}

export { buildReferenceState, resolveLastReferencedEntity };